import React, { useState } from 'react';
import {
  Settings,
  Users,
  Bell,
  Shield,
  Palette,
  Building2,
  Scan,
  Eye,
  ClipboardList,
} from 'lucide-react';
import { cn } from '../../lib/utils';
import GeneralSettings from './GeneralSettings';
import TeamSettings from './TeamSettings';
import NotificationSettings from './NotificationSettings';
import SecuritySettings from './SecuritySettings';
import BrandingSettings from './BrandingSettings';
import ScreeningSettings from './ScreeningSettings';
import VerificationSettings from './VerificationSettings';
import CasePlaybooksSettings from './CasePlaybooksSettings';

const tabs = [
  { id: 'general', label: 'General', icon: Building2 },
  { id: 'team', label: 'Team', icon: Users },
  { id: 'notifications', label: 'Notifications', icon: Bell },
  { id: 'security', label: 'Security', icon: Shield },
  { id: 'screening', label: 'Screening', icon: Scan },
  { id: 'verification', label: 'Verification', icon: Eye },
  { id: 'playbooks', label: 'Case Playbooks', icon: ClipboardList },
  { id: 'branding', label: 'Branding', icon: Palette },
];

export default function SettingsPage() {
  const [activeTab, setActiveTab] = useState('general');

  const renderTabContent = () => {
    switch (activeTab) {
      case 'general':
        return <GeneralSettings />;
      case 'team':
        return <TeamSettings />;
      case 'notifications':
        return <NotificationSettings />;
      case 'security':
        return <SecuritySettings />;
      case 'screening':
        return <ScreeningSettings />;
      case 'verification':
        return <VerificationSettings />;
      case 'playbooks':
        return <CasePlaybooksSettings />;
      case 'branding':
        return <BrandingSettings />;
      default:
        return <GeneralSettings />;
    }
  };

  return (
    <div className="w-full max-w-[1400px]">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <Settings className="w-8 h-8 text-teal-500" />
        <div>
          <h1 className="text-2xl font-semibold text-grey-900 dark:text-grey-50 m-0">
            Settings
          </h1>
          <p className="text-sm text-grey-500 dark:text-grey-400 mt-1">
            Manage your workspace, team, and preferences
          </p>
        </div>
      </div>

      {/* Content */}
      <div className="flex gap-6 flex-col md:flex-row">
        {/* Sidebar */}
        <div className="w-full md:w-[220px] flex-shrink-0">
          <div className="flex flex-col gap-1 bg-white dark:bg-grey-900 rounded-xl p-2 border border-grey-200 dark:border-grey-800 md:flex-col overflow-x-auto">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                className={cn(
                  'flex items-center gap-2.5 px-3.5 py-2.5 rounded-lg cursor-pointer',
                  'text-sm font-medium text-left w-full transition-all duration-150',
                  'border-none bg-transparent font-inherit flex-shrink-0',
                  activeTab === tab.id
                    ? 'bg-teal-50 text-teal-600 dark:bg-teal-950/50 dark:text-teal-400'
                    : 'text-grey-500 hover:bg-grey-100 hover:text-grey-900 dark:text-grey-400 dark:hover:bg-grey-800 dark:hover:text-grey-50'
                )}
                onClick={() => setActiveTab(tab.id)}
                data-tab={tab.id}
              >
                <tab.icon className="w-[18px] h-[18px]" />
                <span>{tab.label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Main Content */}
        <div className="flex-1 min-w-0">
          <div className="bg-white dark:bg-grey-900 rounded-xl border border-grey-200 dark:border-grey-800 p-6">
            {renderTabContent()}
          </div>
        </div>
      </div>
    </div>
  );
}
